/**
 * Track 8: research points (RP), laboratories, research slots and survey discoveries.
 *
 * Everything here is pure (no I/O) so the server, the NPC logic and the tests share one set of rules.
 * Rates are per REAL hour (like the economy); build/upgrade durations are game hours and go through
 * GAME_SPEED on the server (docs/game-clock.md).
 */
import { z } from 'zod';
import { hoursToMs } from '../universe.js';

export const RESEARCH_BALANCE = {
  /** The home world's own scientists: RP per hour while nothing else exists. */
  baseRpPerHour: 1,
  /** A civilization's first RP balance (two tier-1 techs without any lab). */
  startingRp: 8,
  baseSlots: 1,
  maxSlots: 4,
  /** Waiting projects (beyond the running ones) a civilization may keep queued. */
  baseQueue: 2,
  /** Cancelling a RUNNING project refunds this share of what was paid (metal, energy and RP); queued ones pay nothing. */
  cancelRefund: 0.5,
  /** RP cost of a tech by tier. Metal and energy costs stay on top (see TECHS). */
  rpCostByTier: { 1: 4, 2: 16, 3: 48, 4: 120 } as Record<number, number>,
  maxLabs: 4,
  /** A discovery "wreck field" halves the metal and energy price of the next project. */
  wreckDiscount: 0.5,
  /** Lab tiers: build (tier 1) or upgrade (tier N) price, time, output and upkeep. */
  labs: {
    1: { metal: 400, energy: 200, hours: 2, rpPerHour: 4, energyUpkeep: 30 },
    2: { metal: 900, energy: 500, hours: 4, rpPerHour: 10, energyUpkeep: 70 },
    3: { metal: 1800, energy: 1100, hours: 8, rpPerHour: 22, energyUpkeep: 140 },
  } as Record<number, { metal: number; energy: number; hours: number; rpPerHour: number; energyUpkeep: number }>,
  maxLabTier: 3,
} as const;

export const rpCostOfTier = (tier: number): number =>
  RESEARCH_BALANCE.rpCostByTier[tier] ?? RESEARCH_BALANCE.rpCostByTier[4]!;

export const labBuildMs = (tier: number): number => hoursToMs(RESEARCH_BALANCE.labs[tier]?.hours ?? 8);

/** A laboratory as the pure rules see it (times in ms since epoch). */
export interface LabLike {
  tier: number;
  /** Construction finished at (before that the lab produces nothing). */
  activeFrom: number;
  upgradingTo: number | null;
  upgradeAt: number | null;
}

/** The tier a lab actually works at at time `t` (0 = still under construction). */
export function labTierAt(lab: LabLike, t: number): number {
  if (lab.upgradingTo !== null && lab.upgradeAt !== null && t >= lab.upgradeAt) return lab.upgradingTo;
  return t >= lab.activeFrom ? lab.tier : 0;
}

export const labRpPerHour = (tier: number): number => RESEARCH_BALANCE.labs[tier]?.rpPerHour ?? 0;

/** Energy upkeep per hour. It starts with the ORDER (a lab being built or upgraded already draws power). */
export function labEnergyUpkeep(labs: Iterable<LabLike>): number {
  let sum = 0;
  for (const l of labs) sum += RESEARCH_BALANCE.labs[l.upgradingTo ?? l.tier]?.energyUpkeep ?? 0;
  return sum;
}

export interface RpSource { kind: 'home' | 'lab' | 'tech'; label: string; perHour: number }

/** RP per hour at time `t`: home baseline + working labs (× lab output) + flat tech bonuses. */
export function rpRateAt(labs: LabLike[], t: number, flatBonus = 0, labMult = 0): number {
  let rate = RESEARCH_BALANCE.baseRpPerHour + flatBonus;
  for (const l of labs) rate += labRpPerHour(labTierAt(l, t)) * (1 + labMult);
  return rate;
}

/**
 * RP produced between `fromMs` and `toMs`: exact piecewise integration across the moments a lab
 * finishes building or upgrading (so a lab never produces before it is done and never loses output).
 */
export function integrateRp(labs: LabLike[], fromMs: number, toMs: number, flatBonus = 0, labMult = 0): number {
  if (toMs <= fromMs) return 0;
  const cuts = new Set<number>([fromMs, toMs]);
  for (const l of labs) {
    for (const c of [l.activeFrom, l.upgradeAt]) if (c !== null && c > fromMs && c < toMs) cuts.add(c);
  }
  const pts = [...cuts].sort((a, b) => a - b);
  let total = 0;
  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i]!, b = pts[i + 1]!;
    total += rpRateAt(labs, a, flatBonus, labMult) * ((b - a) / 3_600_000);
  }
  return total;
}

/** Highest tier among labs that are working at `t`. */
export const bestLabTierAt = (labs: LabLike[], t: number): number => labs.reduce((m, l) => Math.max(m, labTierAt(l, t)), 0);

/** Parallel research slots: 1, +1 from a tier-2 lab, + tech slots, capped. */
export function researchSlots(labs: LabLike[], t: number, techSlots: number): { total: number; sources: Array<{ label: string; count: number }> } {
  const sources: Array<{ label: string; count: number }> = [{ label: 'Alap', count: RESEARCH_BALANCE.baseSlots }];
  if (bestLabTierAt(labs, t) >= 2) sources.push({ label: '2. szintű laboratórium', count: 1 });
  if (techSlots > 0) sources.push({ label: 'Technológiák', count: techSlots });
  const total = Math.min(RESEARCH_BALANCE.maxSlots, sources.reduce((n, s) => n + s.count, 0));
  return { total, sources };
}

// ---- Discoveries ----

export const DiscoveryKindSchema = z.enum(['archive', 'wreck', 'artifact']);
export type DiscoveryKind = z.infer<typeof DiscoveryKindSchema>;

/** Techs that exist only as survey finds: they cannot be researched. */
export const DISCOVERY_TECH_IDS = ['precursor_lattice', 'void_compass'] as const;

export const DISCOVERY_INFO: Record<DiscoveryKind, { name: string; blurb: string }> = {
  archive: { name: 'Ősi adattár', blurb: 'Letölthető tudományos adatbázis: egyszeri kutatási pont (RP) jutalom.' },
  wreck: { name: 'Roncsmező', blurb: 'Hasznosítható alkatrészek: a következő kutatás fém- és energiaköltsége felére csökken.' },
  artifact: { name: 'Prekurzor-műtárgy', blurb: 'Egyedi technológia, amely normál úton nem kutatható. Csak xenoarcheológiával ismerhető fel.' },
};

export interface DiscoveryRoll {
  kind: DiscoveryKind;
  /** archive: the RP reward. */
  rp?: number;
  /** artifact: the unique tech granted. */
  techId?: string;
}

/** FNV-1a: small, stable, platform independent. */
export function hashString(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193) >>> 0; }
  return h >>> 0;
}

/** Share (percent) of systems that hide a find. */
export const DISCOVERY_CHANCE_PCT = 38;

/**
 * The find hidden in a system — a pure function of the world seed and the system id, so it never
 * changes and costs no storage until somebody surveys the system. `deepScan` (xenoarchaeology) is
 * what makes artifacts visible; without it the roll comes back null for them.
 */
export function discoveryRoll(worldSeed: string, systemId: string, deepScan: boolean): DiscoveryRoll | null {
  const h = hashString(`${worldSeed}:${systemId}:find`);
  if (h % 100 >= DISCOVERY_CHANCE_PCT) return null;
  const pick = (h >>> 8) % 100;
  if (pick < 55) return { kind: 'archive', rp: 6 + ((h >>> 16) % 9) };
  if (pick < 85) return { kind: 'wreck' };
  if (!deepScan) return null;
  return { kind: 'artifact', techId: DISCOVERY_TECH_IDS[(h >>> 16) % DISCOVERY_TECH_IDS.length]! };
}

// ---- API shapes ----

export const LabClientSchema = z.object({
  id: z.string().uuid(),
  systemId: z.string().uuid(),
  systemName: z.string(),
  tier: z.number().int(),
  status: z.enum(['building', 'active', 'upgrading']),
  completesAt: z.string().datetime().nullable(),
  rpPerHour: z.number(),
  energyUpkeep: z.number().int(),
  next: z.object({
    tier: z.number().int(), metal: z.number().int(), energy: z.number().int(), timeMs: z.number().int(),
    rpPerHour: z.number(), energyUpkeep: z.number().int(), blockedBy: z.string().nullable(),
  }).nullable(),
});
export type LabClient = z.infer<typeof LabClientSchema>;

export const DiscoveryClientSchema = z.object({
  id: z.string().uuid(),
  systemId: z.string().uuid(),
  systemName: z.string(),
  kind: DiscoveryKindSchema,
  name: z.string(),
  blurb: z.string(),
  reward: z.string(),
  used: z.boolean(),
  discoveredAt: z.string().datetime(),
});
export type DiscoveryClient = z.infer<typeof DiscoveryClientSchema>;

export const ResearchLogEntrySchema = z.object({
  kind: z.enum(['started', 'queued', 'completed', 'cancelled', 'discovery', 'lab']),
  text: z.string(),
  at: z.string().datetime(),
});
export type ResearchLogEntry = z.infer<typeof ResearchLogEntrySchema>;

export const BuildLabSchema = z.object({ systemId: z.string().uuid(), idempotencyKey: z.string().min(1).max(128) });
export const UpgradeLabSchema = z.object({ labId: z.string().uuid(), idempotencyKey: z.string().min(1).max(128) });
export const CancelResearchSchema = z.object({ techId: z.string().min(1).max(64) });
